import {query} from "@/graphql/client";
import {gql} from "@urql/vue";
import {
    availabilityEffectiveTimesHumanReadable,
    hasShifts,
    hasShiftsOrAAs
} from "@/helper/availability";
import {absenceFragment, getLabelForAbsenceType} from "@/helper/absence";
import {bookingDetailFragment} from "@/helper/booking";
import {Absence, AbsenceType, Availability, Booking, Day} from "@/graphql/generated/graphql";
import { kapitelDateString } from "@/graphql/kapitelTypes";
import { clientFragment, houseFragment, jobsiteFragment, getShortJobsiteDescription } from "./jobsite";
import {computed} from "vue";
import {cmpKapitelDate, endOfMonth, formatKapitelDate} from "@/helper/datetime/date";
import datetime from "@/helper/datetime/datetime";
import {CalendarMonth} from "@/helper/calendar/calendarMonth";
import {graphql} from "@/graphql/generated";

const dayFragment = graphql(`
    fragment Day on Day {
        date
        availability {
            ...Availability
        }
        absence {
            ...AbsenceFragment
        }
        booking {
            ...BookingDetailFragment
        }
        moreDemands {
            id
            begin
            until
            shiftType
            date
            jobsite {
                ...JobsiteFragment
                client {
                    ...ClientFragment
                }
                house {
                    ...HouseFragment
                }
            }
        }
        wishDemands {
            id
            begin
            until
            shiftType
            jobsite {
                ...JobsiteFragment
                client {
                    ...ClientFragment
                }
                house {
                    ...HouseFragment
                }
            }
        }
    }`)

export const dayFragmentDetailed = graphql(`
    fragment DayDetailed on Day {
        ...Day
        availability {
            effectiveJobsites {
                id
            }
        }
    }
`)


export async function fetchDayForDate(date: kapitelDateString) : Promise<Day | undefined> {
    const result = await query(
        graphql(`
            query GetDay ($date: KapitelDateImmutable!) {
                day(
                    date: $date
                ) {
                    ...Day
                }
            }`),
        {
            date: date
        }
    )

    return result?.data?.day;
}

export async function fetchDayDetailedForDate(date: kapitelDateString) : Promise<Day | undefined> {
    const result = await query(
        graphql(`
            query GetDayDetailed ($date: KapitelDateImmutable!) {
                day(
                    date: $date
                ) {
                    ...DayDetailed
                }
            }`),
        {
            date: date
        }
    )

    return result?.data?.day;
}


export type DayMap = Map<kapitelDateString, Day>
export function toDayMap(days: Day[], existingMap: DayMap | undefined = undefined): DayMap {
    const map = existingMap || new Map<kapitelDateString, Day>();
    days.forEach(day => map.set(day.date, day))
    return map;
}

export async function fetchDaysForMonth(month: CalendarMonth): Promise<Day[]>
{
    return fetchDaysForDateRange(month.begin, month.until);
}

export async function fetchDaysForDateRange(min: kapitelDateString, max: kapitelDateString): Promise<Day[]>
{
    const result = await query(
        graphql(`
            query GetDays (
                $min: KapitelDateImmutable!
                $max: KapitelDateImmutable!
            ) {
                days(betweenDates: {max: $max, min: $min}) {
                    ...Day
                }
            }`), {
                min: min,
                max: max
            }
    )

    const days = result?.data?.days;
    if (!days) {
        throw new Error("no days in days response")
    }

    return days
}

export class DayObject {
    constructor(
        public date: kapitelDateString,
        public booking: Booking|undefined,
        public absence: Absence|undefined,
        public availability: Availability|undefined,
    ) { }
}

export const objectify = (day: Day) : DayObject => new DayObject(
    day.date,
    day.booking || undefined,
    day.absence || undefined,
    day.availability || undefined
)

export async function fetchDayObjectsForDateRange(min: kapitelDateString, max: kapitelDateString): Promise<Array<DayObject>> {
    const days = await fetchDaysForDateRange(min, max)
    return days.map(objectify)
}

export const yieldEffectiveAvailabilities = (days: (Day|DayObject)[]): Availability[] => {
    return days.reduce((arr:Availability[], day) => {
        if (day.availability) {
            arr.push(day.availability)
        }
        return arr
    }, [])
}

export const yieldEffectiveAbsences = (days: (Day|DayObject)[]): Absence[] => {
    return days.reduce((arr:Absence[], day) => {
        if (day.absence) {
            arr.push(day.absence)
        }
        return arr
    }, [])
}

export const yieldEffectiveBookings = (days: (Day|DayObject)[]): Booking[] => {
    return days.reduce((arr:Booking[], day) => {
        if (day.booking) {
            arr.push(day.booking)
        }
        return arr
    }, [])
}

export function generateDay(
    date: kapitelDateString
): Day {
    return <Day>{
        date: date,
    }
}

export const sorter = (day1:Day, day2:Day) => cmpKapitelDate(day1.date, day2.date)

enum NotAvailabilityableReason {
    HasAbsence = "HasAbsence",
    HasBooking = "HasBooking",
}

export type AvailabilityChangeAttemptResult =
    | { success: true }
    | { success: false; reason: NotAvailabilityableReason };

export const canAddAvailabilityToDay = (day:Day): AvailabilityChangeAttemptResult => {

    if(day.booking) {
        return {
            success: false,
            reason: NotAvailabilityableReason.HasBooking,
        }
    }
    if(day.absence) {
        return {
            success: false,
            reason: NotAvailabilityableReason.HasAbsence,
        }
    }

    return {
        success: true,
    }
}

export const convertDaysToTextLines = (days:Array<Day>): Array<string> => {
    const result: string[] = []

    days.forEach(day => {
        if (!day.booking && !day.absence && (!day.availability || !hasShiftsOrAAs(day.availability))) {
            return
        }

        const date = formatKapitelDate(day.date)
        result.push(date)

        if (day.booking) {
            const begin = datetime.formatKapitelDateTime(day.booking.begin, datetime.kapitelDateTimeFormatTime)
            const until = datetime.formatKapitelDateTime(day.booking.until, datetime.kapitelDateTimeFormatTime)
            const jobsite = getShortJobsiteDescription(day.booking.jobsite, false, ' - ')

            result.push(`Dienst ${begin} - ${until} Uhr`)
            result.push(jobsite)
        }
        else if (day.absence) {
            const type = getLabelForAbsenceType(day.absence.type)

            result.push('Nicht verfügbar')
            result.push(type)
        }
        else if (day.availability) {
            const avStr = availabilityEffectiveTimesHumanReadable(day.availability)

            if (avStr) {
                result.push('Verfügbar')
                result.push(avStr)
            }
        }

        result.push('\n')
    })

    if(result.length == 0){
        result.push('Keine Einträge')
    }

    return result
}
